import { Component, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CreateProjectService } from '../../services/create-project.service';

interface MatchedMember {
  id: any;
  name: string;
  email: string;
}

interface MatchedDepartment {
  id: string;
  name: string;
  count?: number;
  isFavorite?: boolean;
  selected: boolean;
  members: MatchedMember[];
}

const EMAIL_REGEX = /^[^\s@,;:]+@[^\s@,;:]+\.[^\s@,;:]+$/;  // NOSONAR


@Component({
  selector: 'app-search-user-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule],
  templateUrl: './search-user-modal.component.html',
  styleUrl: './search-user-modal.component.css'
})
export class SearchUserModalComponent {
  searchInput = '';
  invalidTokens: string[] = [];
  noMatchEmails: string[] = [];
  matchedDepartments: MatchedDepartment[] = [];
  isSearching = false;
  hasSearched = false;
  private searchedEmails: string[] = [];

  constructor(
    private readonly dialogRef: MatDialogRef<SearchUserModalComponent>,
    private readonly createProjectService: CreateProjectService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  get hasSelectedDepartments(): boolean {
    return this.matchedDepartments.some(d => d.selected);
  }

  onSearch(): void {
    if (this.isSearching || !this.searchInput.trim()) { return; }

    const tokens = this.searchInput
      .split(/[,;]+/)
      .map(t => t.trim())
      .filter(Boolean);

    const validEmails = Array.from(new Set(tokens.filter(t => EMAIL_REGEX.test(t))));
    this.invalidTokens = tokens.filter(t => !EMAIL_REGEX.test(t));

    if (!validEmails.length) {
      this.matchedDepartments = [];
      this.noMatchEmails = [];
      this.hasSearched = false;
      return;
    }

    this.isSearching = true;
    this.hasSearched = false;
    this.matchedDepartments = [];
    this.noMatchEmails = [];
    this.searchedEmails = validEmails;

    // Combine all emails into a single search call instead of one request per email.
    const combinedSearchText = validEmails.join(',');

    this.createProjectService.fetchDepartments(null, 1, 'asc', combinedSearchText).pipe(
      map((resp: any) => resp?.payload || []),
      catchError(() => of([]))
    ).subscribe(departments => {
      const deptMap = new Map<string, MatchedDepartment>();
      departments.forEach((d: any) => {
        const id = String(d.dept_id ?? d.id);
        if (!deptMap.has(id)) {
          deptMap.set(id, {
            id,
            name: String(d.department_name ?? d.name ?? ''),
            count: Number(d.team_member_count ?? d.count ?? 0),
            isFavorite: this.toBooleanFlag(d.is_favourite ?? d.is_favorite),
            selected: true,
            members: []
          });
        }
      });
      this.matchedDepartments = Array.from(deptMap.values());
      this.noMatchEmails = this.matchedDepartments.length ? [] : validEmails;

      if (!this.matchedDepartments.length) {
        this.isSearching = false;
        this.hasSearched = true;
        return;
      }

      // For each matched department, fetch its members and keep only the ones
      // whose email was searched, so they can be shown as already-selected.
      const emailsLower = new Set(validEmails.map(e => e.toLowerCase()));
      forkJoin(
        this.matchedDepartments.map(dept => this.fetchMatchedMembersForDept(dept.id, emailsLower))
      ).subscribe(membersPerDept => {
        this.matchedDepartments.forEach((dept, idx) => { dept.members = membersPerDept[idx]; });
        this.isSearching = false;
        this.hasSearched = true;
      });
    });
  }

  private fetchMatchedMembersForDept(deptId: string, emailsLower: Set<string>) {
    return this.createProjectService.fetchTeamMembers(deptId, null).pipe(
      map((members: any[]) =>
        (members || [])
          .filter((m: any) => emailsLower.has(String(m.tm_email || m.email || m.tm_mail || '').trim().toLowerCase()))
          .map((m: any) => ({
            id: m.tm_id,
            name: String(m.tm_name || ''),
            email: String(m.tm_email || m.email || m.tm_mail || '')
          }))
      ),
      catchError(() => of([]))
    );
  }


  toggleDepartment(dept: MatchedDepartment): void {
    dept.selected = !dept.selected;
  }

  private toBooleanFlag(value: any): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }
    return value === true || value === 1;
  }

  confirm(): void {
    this.dialogRef.close({
      departments: this.matchedDepartments.filter(d => d.selected),
      emails: this.searchedEmails
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}

